import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Users, 
  User, 
  Mail, 
  Phone, 
  Linkedin, 
  Building, 
  Code, 
  Palette, 
  Smartphone, 
  Shield, 
  BarChart3, 
  Lightbulb,
  ChevronDown,
  ChevronRight,
  ExternalLink,
  Crown,
  Star,
  Award,
  Zap,
  Target,
  Globe,
  Server,
  Database,
  Wrench,
  BookOpen,
  Play,
  Palette as PaletteIcon,
  Smartphone as SmartphoneIcon,
  Shield as ShieldIcon,
  BarChart3 as BarChart3Icon,
  Lightbulb as LightbulbIcon
} from 'lucide-react';

const Team = ({ user }) => {
  const [expandedDepartments, setExpandedDepartments] = useState(new Set(['leadership', 'development', 'design', 'qa', 'pm']));
  const [selectedMember, setSelectedMember] = useState(null);

  // Datos del equipo organizados por departamentos
  const teamData = {
    leadership: {
      name: 'Liderazgo Ejecutivo',
      icon: Crown,
      color: 'from-yellow-500 to-orange-600',
      members: [
        {
          id: 'ceo',
          name: 'María González',
          position: 'CEO & Fundadora',
          email: 'maria.gonzalez@innovadev.com',
          phone: '+54 11 1234-5678',
          linkedin: 'https://linkedin.com/in/mariagonzalez',
          avatar: 'https://images.unsplash.com/photo-1494790108755-2616b612b786?w=150&h=150&fit=crop&crop=face',
          department: 'Liderazgo',
          experience: '15+ años',
          skills: ['Estrategia Empresarial', 'Liderazgo', 'Innovación', 'Finanzas'],
          bio: 'Apasionada por la tecnología y la innovación. Lidera la visión estratégica de Innova Developers con más de 15 años de experiencia en el sector tech.'
        },
        {
          id: 'cto',
          name: 'Carlos Rodríguez',
          position: 'CTO & Co-Fundador',
          email: 'carlos.rodriguez@innovadev.com',
          phone: '+54 11 1234-5679',
          linkedin: 'https://linkedin.com/in/carlosrodriguez',
          avatar: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=150&h=150&fit=crop&crop=face',
          department: 'Liderazgo',
          experience: '12+ años',
          skills: ['Arquitectura de Software', 'DevOps', 'Cloud Computing', 'Machine Learning'],
          bio: 'Experto en tecnologías emergentes y arquitectura de sistemas escalables. Impulsa la innovación tecnológica en la empresa.'
        },
        {
          id: 'cpo',
          name: 'Ana Martínez',
          position: 'CPO & Head of Design',
          email: 'ana.martinez@innovadev.com',
          phone: '+54 11 1234-5680',
          linkedin: 'https://linkedin.com/in/anamartinez',
          avatar: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=150&h=150&fit=crop&crop=face',
          department: 'Liderazgo',
          experience: '10+ años',
          skills: ['Product Strategy', 'UX/UI Design', 'User Research', 'Product Management'],
          bio: 'Especialista en experiencia de usuario y estrategia de productos. Asegura que nuestros productos sean intuitivos y valiosos.'
        }
      ]
    },
    development: {
      name: 'Desarrollo',
      icon: Code,
      color: 'from-blue-500 to-purple-600',
      members: [
        {
          id: 'lead-dev',
          name: 'Diego Fernández',
          position: 'Lead Developer',
          email: 'diego.fernandez@innovadev.com',
          phone: '+54 11 1234-5681',
          linkedin: 'https://linkedin.com/in/diegofernandez',
          avatar: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=150&h=150&fit=crop&crop=face',
          department: 'Desarrollo',
          experience: '8+ años',
          skills: ['React', 'Node.js', 'Python', 'AWS', 'Team Leadership'],
          bio: 'Desarrollador full-stack senior con pasión por el código limpio y las mejores prácticas. Lidera el equipo de desarrollo.'
        },
        {
          id: 'senior-backend',
          name: 'Laura Silva',
          position: 'Senior Backend Developer',
          email: 'laura.silva@innovadev.com',
          phone: '+54 11 1234-5682',
          linkedin: 'https://linkedin.com/in/laurasilva',
          avatar: 'https://images.unsplash.com/photo-1544005313-94ddf0286df2?w=150&h=150&fit=crop&crop=face',
          department: 'Desarrollo',
          experience: '6+ años',
          skills: ['Java', 'Spring Boot', 'PostgreSQL', 'Docker', 'Microservices'],
          bio: 'Especialista en arquitectura backend y sistemas distribuidos. Experta en Java y tecnologías empresariales.'
        },
        {
          id: 'senior-frontend',
          name: 'Roberto López',
          position: 'Senior Frontend Developer',
          email: 'roberto.lopez@innovadev.com',
          phone: '+54 11 1234-5683',
          linkedin: 'https://linkedin.com/in/robertolopez',
          avatar: 'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=150&h=150&fit=crop&crop=face',
          department: 'Desarrollo',
          experience: '7+ años',
          skills: ['React', 'Vue.js', 'TypeScript', 'CSS3', 'Performance'],
          bio: 'Desarrollador frontend experto en crear interfaces modernas y responsivas. Especialista en optimización de rendimiento.'
        },
        {
          id: 'mobile-dev',
          name: 'Camila Torres',
          position: 'Mobile Developer',
          email: 'camila.torres@innovadev.com',
          phone: '+54 11 1234-5684',
          linkedin: 'https://linkedin.com/in/camilatorres',
          avatar: 'https://images.unsplash.com/photo-1487412720507-e7ab37603c6f?w=150&h=150&fit=crop&crop=face',
          department: 'Desarrollo',
          experience: '5+ años',
          skills: ['React Native', 'Flutter', 'iOS', 'Android', 'Mobile UX'],
          bio: 'Desarrolladora móvil apasionada por crear apps intuitivas y funcionales. Experta en React Native y Flutter.'
        },
        {
          id: 'devops',
          name: 'Pablo Herrera',
          position: 'DevOps Engineer',
          email: 'pablo.herrera@innovadev.com',
          phone: '+54 11 1234-5685',
          linkedin: 'https://linkedin.com/in/pabloherrera',
          avatar: 'https://images.unsplash.com/photo-1506794778202-cad84cf45f1d?w=150&h=150&fit=crop&crop=face',
          department: 'Desarrollo',
          experience: '6+ años',
          skills: ['AWS', 'Docker', 'Kubernetes', 'CI/CD', 'Infrastructure'],
          bio: 'Ingeniero DevOps especializado en automatización y escalabilidad. Mantiene nuestra infraestructura robusta y eficiente.'
        }
      ]
    },
    design: {
      name: 'Diseño & UX',
      icon: Palette,
      color: 'from-pink-500 to-red-600',
      members: [
        {
          id: 'ux-lead',
          name: 'Valentina Ruiz',
          position: 'UX/UI Lead Designer',
          email: 'valentina.ruiz@innovadev.com',
          phone: '+54 11 1234-5686',
          linkedin: 'https://linkedin.com/in/valentinaruiz',
          avatar: 'https://images.unsplash.com/photo-1494790108755-2616b612b786?w=150&h=150&fit=crop&crop=face',
          department: 'Diseño',
          experience: '8+ años',
          skills: ['UX Research', 'UI Design', 'Prototyping', 'Design Systems', 'Figma'],
          bio: 'Diseñadora UX/UI con enfoque en investigación de usuarios y diseño centrado en el ser humano.'
        },
        {
          id: 'ui-designer',
          name: 'Santiago Morales',
          position: 'UI Designer',
          email: 'santiago.morales@innovadev.com',
          phone: '+54 11 1234-5687',
          linkedin: 'https://linkedin.com/in/santiagomorales',
          avatar: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=150&h=150&fit=crop&crop=face',
          department: 'Diseño',
          experience: '5+ años',
          skills: ['Visual Design', 'Branding', 'Illustration', 'Motion Graphics'],
          bio: 'Diseñador visual creativo especializado en branding y diseño de interfaces atractivas.'
        }
      ]
    },
    qa: {
      name: 'QA & Testing',
      icon: Shield,
      color: 'from-green-500 to-teal-600',
      members: [
        {
          id: 'qa-lead',
          name: 'Natalia Castro',
          position: 'QA Lead Engineer',
          email: 'natalia.castro@innovadev.com',
          phone: '+54 11 1234-5688',
          linkedin: 'https://linkedin.com/in/nataliacastro',
          avatar: 'https://images.unsplash.com/photo-1487412720507-e7ab37603c6f?w=150&h=150&fit=crop&crop=face',
          department: 'QA',
          experience: '7+ años',
          skills: ['Test Automation', 'Selenium', 'Jest', 'Performance Testing', 'Test Strategy'],
          bio: 'Ingeniera QA experta en automatización y estrategias de testing. Asegura la calidad de nuestros productos.'
        },
        {
          id: 'qa-engineer',
          name: 'Matías Vega',
          position: 'QA Engineer',
          email: 'matias.vega@innovadev.com',
          phone: '+54 11 1234-5689',
          linkedin: 'https://linkedin.com/in/matiasvega',
          avatar: 'https://images.unsplash.com/photo-1506794778202-cad84cf45f1d?w=150&h=150&fit=crop&crop=face',
          department: 'QA',
          experience: '4+ años',
          skills: ['Manual Testing', 'API Testing', 'Mobile Testing', 'Bug Tracking'],
          bio: 'Ingeniero QA especializado en testing manual y automatizado. Detector experto de bugs.'
        }
      ]
    },
    pm: {
      name: 'Project Management',
      icon: Target,
      color: 'from-indigo-500 to-blue-600',
      members: [
        {
          id: 'senior-pm',
          name: 'Gabriela Ortiz',
          position: 'Senior Project Manager',
          email: 'gabriela.ortiz@innovadev.com',
          phone: '+54 11 1234-5690',
          linkedin: 'https://linkedin.com/in/gabrielaortiz',
          avatar: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=150&h=150&fit=crop&crop=face',
          department: 'PM',
          experience: '9+ años',
          skills: ['Agile', 'Scrum', 'Risk Management', 'Stakeholder Management', 'Jira'],
          bio: 'Project Manager senior con amplia experiencia en metodologías ágiles y gestión de proyectos complejos.'
        },
        {
          id: 'pm',
          name: 'Lucas Mendoza',
          position: 'Project Manager',
          email: 'lucas.mendoza@innovadev.com',
          phone: '+54 11 1234-5691',
          linkedin: 'https://linkedin.com/in/lucasmendoza',
          avatar: 'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=150&h=150&fit=crop&crop=face',
          department: 'PM',
          experience: '5+ años',
          skills: ['Project Planning', 'Team Coordination', 'Budget Management', 'Client Communication'],
          bio: 'Project Manager especializado en coordinación de equipos y comunicación con clientes.'
        }
      ]
    }
  };

  // Función para expandir/contraer departamento
  const toggleDepartment = (deptKey) => {
    setExpandedDepartments(prev => {
      const newSet = new Set(prev);
      if (newSet.has(deptKey)) {
        newSet.delete(deptKey);
      } else {
        newSet.add(deptKey);
      }
      return newSet;
    });
  };

  // Función para obtener el ícono por nombre
  const getIconByName = (iconName) => {
    const iconMap = {
      'Code': Code,
      'Palette': PaletteIcon,
      'Shield': ShieldIcon,
      'Target': Target,
      'Crown': Crown
    };
    return iconMap[iconName] || Users;
  };

  return (
    <div className="min-h-screen bg-background p-4">
      <div className="container mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <h1 className="text-4xl font-bold gradient-text mb-2">
            Nuestro Equipo 👥
          </h1>
          <p className="text-text-secondary text-lg">
            Conoce a los profesionales apasionados que hacen posible la innovación en Innova Developers
          </p>
        </motion.div>

        {/* Estadísticas del equipo */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8"
        >
          {[
            { label: 'Total de Miembros', value: Object.values(teamData).reduce((total, dept) => total + dept.members.length, 0), icon: Users, color: 'from-primary to-accent' },
            { label: 'Departamentos', value: Object.keys(teamData).length, icon: Building, color: 'from-secondary to-purple-600' },
            { label: 'Años Promedio', value: '8+', icon: Award, color: 'from-yellow-500 to-orange-600' },
            { label: 'Proyectos Completados', value: '150+', icon: Star, color: 'from-green-500 to-emerald-600' }
          ].map((stat, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 + index * 0.1 }}
              className="card hover-lift"
            >
              <div className={`w-12 h-12 bg-gradient-to-br ${stat.color} rounded-lg flex items-center justify-center mb-4`}>
                <stat.icon className="w-6 h-6 text-white" />
              </div>
              <h3 className="text-2xl font-bold text-text mb-1">{stat.value}</h3>
              <p className="text-text-secondary text-sm">{stat.label}</p>
            </motion.div>
          ))}
        </motion.div>

        {/* Organigrama */}
        <div className="space-y-6">
          {Object.entries(teamData).map(([deptKey, department]) => (
            <motion.div
              key={deptKey}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.4 }}
              className="card"
            >
              {/* Header del departamento */}
              <button
                onClick={() => toggleDepartment(deptKey)}
                className="w-full flex items-center justify-between p-6 hover:bg-gray-800/50 transition-colors rounded-lg"
              >
                <div className="flex items-center space-x-4">
                  <div className={`w-16 h-16 bg-gradient-to-br ${department.color} rounded-xl flex items-center justify-center`}>
                    {(() => {
                      const IconComponent = getIconByName(department.icon.name);
                      return <IconComponent className="w-8 h-8 text-white" />;
                    })()}
                  </div>
                  <div className="text-left">
                    <h2 className="text-2xl font-bold text-text">{department.name}</h2>
                    <p className="text-text-secondary">
                      {department.members.length} miembros del equipo
                    </p>
                  </div>
                </div>
                
                {expandedDepartments.has(deptKey) ? (
                  <ChevronDown className="w-6 h-6 text-text-secondary" />
                ) : (
                  <ChevronRight className="w-6 h-6 text-text-secondary" />
                )}
              </button>

              {/* Miembros del departamento */}
              <AnimatePresence>
                {expandedDepartments.has(deptKey) && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                    transition={{ duration: 0.3 }}
                    className="px-6 pb-6"
                  >
                    <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6 mt-6">
                      {department.members.map((member, index) => (
                        <motion.div
                          key={member.id}
                          initial={{ opacity: 0, y: 20 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: index * 0.1 }}
                          className="bg-surface border border-gray-700 rounded-lg p-6 hover:border-primary transition-colors cursor-pointer"
                          onClick={() => setSelectedMember(member)}
                        >
                          {/* Avatar y información básica */}
                          <div className="flex items-center space-x-4 mb-4">
                            <img
                              src={member.avatar}
                              alt={member.name}
                              className="w-16 h-16 rounded-full border-2 border-primary/20"
                            />
                            <div className="flex-1">
                              <h3 className="text-lg font-semibold text-text">{member.name}</h3>
                              <p className="text-primary font-medium">{member.position}</p>
                              <p className="text-text-secondary text-sm">{member.experience} de experiencia</p>
                            </div>
                          </div>

                          {/* Información de contacto */}
                          <div className="space-y-2 mb-4">
                            <div className="flex items-center space-x-2 text-sm text-text-secondary">
                              <Mail className="w-4 h-4" />
                              <span className="truncate">{member.email}</span>
                            </div>
                            <div className="flex items-center space-x-2 text-sm text-text-secondary">
                              <Phone className="w-4 h-4" />
                              <span>{member.phone}</span>
                            </div>
                          </div>

                          {/* Skills */}
                          <div className="mb-4">
                            <p className="text-xs text-text-secondary mb-2">Habilidades principales:</p>
                            <div className="flex flex-wrap gap-1">
                              {member.skills.slice(0, 3).map((skill, skillIndex) => (
                                <span
                                  key={skillIndex}
                                  className="px-2 py-1 bg-primary/10 text-primary rounded-full text-xs"
                                >
                                  {skill}
                                </span>
                              ))}
                              {member.skills.length > 3 && (
                                <span className="px-2 py-1 bg-gray-700 text-text-secondary rounded-full text-xs">
                                  +{member.skills.length - 3}
                                </span>
                              )}
                            </div>
                          </div>

                          {/* LinkedIn */}
                          <div className="flex justify-between items-center">
                            <a
                              href={member.linkedin}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="flex items-center space-x-2 text-primary hover:text-accent transition-colors"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <Linkedin className="w-4 h-4" />
                              <span className="text-sm">Ver perfil</span>
                            </a>
                            <span className="text-xs text-text-secondary">
                              {member.department}
                            </span>
                          </div>
                        </motion.div>
                      ))}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          ))}
        </div>

        {/* Modal de detalle del miembro */}
        <AnimatePresence>
          {selectedMember && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
              onClick={() => setSelectedMember(null)}
            >
              <motion.div
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.9, opacity: 0 }}
                className="bg-surface border border-gray-700 rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
                onClick={(e) => e.stopPropagation()}
              >
                {/* Header del modal */}
                <div className="p-6 border-b border-gray-700">
                  <div className="flex items-center space-x-4">
                    <img
                      src={selectedMember.avatar}
                      alt={selectedMember.name}
                      className="w-20 h-20 rounded-full border-2 border-primary/20"
                    />
                    <div>
                      <h2 className="text-2xl font-bold text-text">{selectedMember.name}</h2>
                      <p className="text-primary font-medium text-lg">{selectedMember.position}</p>
                      <p className="text-text-secondary">{selectedMember.department} • {selectedMember.experience}</p>
                    </div>
                  </div>
                </div>

                {/* Contenido del modal */}
                <div className="p-6 space-y-6">
                  {/* Bio */}
                  <div>
                    <h3 className="text-lg font-semibold text-text mb-3">Biografía</h3>
                    <p className="text-text-secondary leading-relaxed">{selectedMember.bio}</p>
                  </div>

                  {/* Habilidades */}
                  <div>
                    <h3 className="text-lg font-semibold text-text mb-3">Habilidades</h3>
                    <div className="flex flex-wrap gap-2">
                      {selectedMember.skills.map((skill, index) => (
                        <span
                          key={index}
                          className="px-3 py-1 bg-primary/20 text-primary rounded-full text-sm"
                        >
                          {skill}
                        </span>
                      ))}
                    </div>
                  </div>

                  {/* Información de contacto */}
                  <div>
                    <h3 className="text-lg font-semibold text-text mb-3">Información de Contacto</h3>
                    <div className="space-y-3">
                      <div className="flex items-center space-x-3">
                        <Mail className="w-5 h-5 text-text-secondary" />
                        <span className="text-text-secondary">{selectedMember.email}</span>
                      </div>
                      <div className="flex items-center space-x-3">
                        <Phone className="w-5 h-5 text-text-secondary" />
                        <span className="text-text-secondary">{selectedMember.phone}</span>
                      </div>
                      <div className="flex items-center space-x-3">
                        <Linkedin className="w-5 h-5 text-primary" />
                        <a
                          href={selectedMember.linkedin}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-primary hover:text-accent transition-colors"
                        >
                          Ver perfil en LinkedIn
                        </a>
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default Team;
